<template>
  <div class="main-content">
    <v-container v-if="isLoading" class="center-container">
      <div style="width: 100%">
        <LoadingComponent
          :progress="socketProgress"
          :socket-message="socketMessage"
          :use-socket="true"
        >
        </LoadingComponent>
      </div>
    </v-container>

    <v-container v-else-if="!isLoading && !lessonStorage">
      <div style="margin-bottom: 1.5em">
        <PhaseComponent
          :selectedNumber="generatePhase"
          @phase-clicked="handlePhaseClick"
        ></PhaseComponent>
      </div>

      <v-row class="justify-center">
        <v-col cols="12" md="8">
          <PromptEditor></PromptEditor>
        </v-col>
      </v-row>
    </v-container>

    <div
      v-if="lessonStorage && !isLoading"
      style="
        width: 100%;
        background-color: #d8dae5;
        height: 100px;
        margin-bottom: 50px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding-left: 40px;
      "
    >
      <div style="display: flex; align-items: center; gap: 9px">
        <img
          src="/images/arrow-back.svg"
          style="cursor: pointer"
          @click="handleBack"
          alt=""
        />
        <h2
          style="
            color: var(--primary-blue);
            font-size: var(--fs-xl);
            font-weight: 400;
            margin-top: 5px;
          "
        >
          Back to Admin Panel
        </h2>
      </div>
      <p style="font-size: var(--fs-big); font-weight: 400; color: #003d6a">
        You are viewing the original Lesson Plan Generated by AI
      </p>
    </div>

    <GeneratedComponent
      v-show="!isLoading || !hasError"
      ref="generatedComponentRef"
      @loading="handleLoading"
      @error="handleError"
    ></GeneratedComponent>

    <template v-if="hasError && !apiCallError">
      <ErrorGenerate
        ref="errorGenerateRef"
        @retry="callRetry"
        :title="errorTitle"
        :message="errorMessage"
        :show-retry="true"
      ></ErrorGenerate>
    </template>
  </div>
</template>

<script setup>
import ErrorGenerate from "@/components/ErrorGenerate.vue";
import { connectSocket, disconnectSocket, isSocketConnected } from "@/socket";
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import GeneratedComponent from "../components/GeneratedComponent.vue";
import LoadingComponent from "../components/LoadingComponent.vue";
import PhaseComponent from "../components/PhaseComponent.vue";
import PromptEditor from "../components/PromptEditor.vue";

const router = useRouter();
const generatePhase = ref(1);
const isLoading = ref(false);
const socketMessage = ref("Building lesson...");
const socketProgress = ref(0);
const currentResourceId = ref(null);
const errorTitle = ref("");
const errorMessage = ref("");
const hasError = ref(false);
const generatedComponentRef = ref(null);
const apiCallError = ref(false);

function callRetry() {
  hasError.value = false;
  errorTitle.value = "";
  errorMessage.value = "";
  generatedComponentRef.value?.handleRetry();
}

const route = useRoute();
const lessonStorage = localStorage.getItem("lesson-storage");

const handleBack = async () => {
  localStorage.removeItem("lesson-storage");
  router.back();
};

const setGeneratePhase = (newPhase) => {
  generatePhase.value = newPhase;
};

onMounted(() => {
  setGeneratePhase(3);
});

const cleanupSocket = () => {
  if (currentResourceId.value) {
    const socket = connectSocket();
    socket.off(currentResourceId.value);
    currentResourceId.value = null;
  }
  disconnectSocket();
};

onBeforeUnmount(() => {
  cleanupSocket();
});

const handlePhaseClick = (phaseNumber) => {
  if (phaseNumber === 2) {
    router.push({ path: "/", query: { key: "step-2" } });
  } else if (phaseNumber === 1) {
    router.push({ path: "/", query: { key: "reset" } });
  }
};

const setupSocketListener = (socket, resourceId) => {
  if (!socket || !resourceId) return;

  // Remove any existing listeners for this resource
  socket.off(resourceId);

  // Add new listener
  socket.on(resourceId, (socketData) => {
    console.log("Received socket data:", socketData);
    socketMessage.value = socketData.message;

    if (socketData.message === "Failed to build lesson") {
      socketMessage.value = "Failed to build lesson, trying to build again...";
    }
    socketProgress.value = socketData.progress;

    if (
      socketData.build_success === true ||
      socketData.build_success === false
    ) {
      setTimeout(function () {
        isLoading.value = false;
        if (socketData.build_success === true) {
          hasError.value = false;
          callRetry();
          // window.location.reload()
        } else if (socketData.build_success === false) {
          hasError.value = true;
          errorTitle.value = "Oops! Something Went Wrong";
          errorMessage.value =
            "We encountered an unexpected error in generating this lesson. Please rephrase your prompt and try again.";
          console.log("Lesson generation failed. Error: ", socketData.message);
        }
      }, 2000);
    }
  });
};

const handleLoading = (isLoadingValue) => {
  isLoading.value = isLoadingValue;

  // Clean up previous socket listener if exists
  cleanupSocket();

  if (isLoadingValue) {
    const encryptedString = route.query.data;
    if (encryptedString) {
      try {
        const decryptedString = decodeURIComponent(encryptedString);
        const data = JSON.parse(decryptedString);

        if (data?.resource_id) {
          socketMessage.value = "Building lesson...";
          const socket = connectSocket();
          currentResourceId.value = data.resource_id;

          // Setup initial connection handler if socket is not connected
          if (!isSocketConnected()) {
            socket.on("connect", () => {
              console.log("Socket connected, setting up listener");
              setupSocketListener(socket, data.resource_id);
            });
          } else {
            // If already connected, setup listener immediately
            setupSocketListener(socket, data.resource_id);
          }

          // Handle reconnection cases
          socket.on("reconnect", () => {
            console.log("Socket reconnected, setting up listener");
            setupSocketListener(socket, data.resource_id);
          });
        }
      } catch (error) {
        console.error("Error processing data:", error);
        socketMessage.value = "Error connecting to server";
        // cleanupSocket()
      }
    }
  }
};

const handleError = (errorData) => {
  console.log("apiCallError", apiCallError.value);

  // If GeneratedComponent sends error data, use it for the parent error display
  if (errorData && typeof errorData === "object") {
    hasError.value = true;
    errorTitle.value = errorData.title || "Error occurred";
    errorMessage.value = errorData.message || "An unexpected error occurred";
  } else {
    // Fallback for simple error emission
    hasError.value = true;
    errorTitle.value = "Error occurred";
    errorMessage.value = "An unexpected error occurred";
  }

  apiCallError.value = true;
  cleanupSocket();
};
</script>

<style scoped>
.main-content {
  position: relative;
  /* display: flex; */
  /* justify-content: center; */
}

.custom-tab {
  padding-bottom: 5px;
  font-size: var(--fs-medium) !important;
  text-transform: none !important;
}

.custom-tabs {
  width: fit-content;
  color: #cccccc;

  &:hover {
    background-color: white;
  }
}

.custom-tabs.active {
  width: 50px;
  height: 3px;
  background-color: #e00069;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  background-color: 1px solid #e00069;
}

.button-edit {
  background-color: #00925e !important;
  color: white !important;
  text-transform: none !important;
  font-size: var(--fs-big) !important;
  font-weight: 600 !important;
  border-radius: 15px !important;
  width: 100px !important;
  height: 50px !important;

  &:hover {
    background-color: #00734a !important;
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  color: #3b4648;
  font-size: var(--fs-big);
  font-weight: 600;
}

th,
td {
  padding: 16px;
  text-align: left;
  border: 1px solid #d8dae5;
  text-align: left;
  vertical-align: top;
}

th {
  background-color: #f5f6fa;
  font-weight: bold;
}

td ul {
  padding-left: 20px;
  margin: 0;
}

.panel-header {
  background-color: #00925e;
  color: white;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start !important;
}

.detail-card {
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  background-color: #f2f3fb !important;
  border-radius: 15px !important;
  padding: 20px !important;
  margin-top: 20px !important;
  height: 120px !important;
}

.detail-content {
  display: flex;
  align-items: center;
}

.detail-content v-icon {
  margin-right: 10px;
}

.detail-text {
  margin: 0;
  color: #666;
}

/* siidebar*/
.sidebar {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 50px;
  transition: width 0.3s ease;
  background-color: white;
  border-left: 1px solid #ddd;
}

/* .sidebar--expanded {
    width: 250px;
  } */

.toggle-button {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 10px;
  color: #00925e;
  margin-top: 100px;
}

.content {
  padding: 10px;
  width: 100%;
}

.actions {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.discard-button {
  background-color: #e00069;
  color: white;
  padding: 10px;
  border: none;
  border-radius: 15px;
}

.save-button {
  background-color: #00925e;
  color: white;
  padding: 10px;
  border: none;
  border-radius: 15px;
}

.section-title {
  font-weight: bold;
  color: #00925e;
  margin-top: 3px;
  font-size: 20 !important;
  font-weight: 600;
  margin-left: 10px;
}

ul {
  padding: 0;
}

li {
  margin: 10px 0;
}

strong {
  font-weight: bold;
}
</style>
